import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export interface DropdownMenuApi {
  close: () => void;
  /** In single-select mode, call after choosing an item to close the menu */
  pick: () => void;
  multiple: boolean;
  open: boolean;
}

export interface DropdownProps {
  /** Default menu width; ignored when `matchTriggerWidth` is true */
  width?: number;
  matchTriggerWidth?: boolean;
  estimatedHeight?: number;
  offset?: number;
  triggerClassName?: string;
  menuClassName?: string;
  multiple?: boolean;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Trigger content; entire root is clickable to toggle (same as Svelte `dropdown.svelte`).
   */
  trigger: (api: {
    open: boolean;
    toggle: () => void;
    close: () => void;
  }) => React.ReactNode;
  /**
   * Scrollable list body (header/footer are optional).
   */
  children: (api: DropdownMenuApi) => React.ReactNode;
  header?: (api: { close: () => void }) => React.ReactNode;
  footer?: (api: { close: () => void }) => React.ReactNode;
}

/**
 * Port of `src/lib/dropdown/component/dropdown.svelte`: fixed-position listbox, smart placement, click outside to close.
 */
export function Dropdown({
  width: widthProp = 200,
  matchTriggerWidth = true,
  estimatedHeight = 320,
  offset = 8,
  triggerClassName = "",
  menuClassName = "",
  multiple = false,
  id: idProp,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  trigger: renderTrigger,
  children: renderMenu,
  header: renderHeader,
  footer: renderFooter,
}: DropdownProps) {
  const genId = useId();
  const listboxId = idProp ?? `dd-${genId.replace(/:/g, "")}`;

  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = isControlled ? (controlledOpen as boolean) : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const close = useCallback(() => setOpen(false), [setOpen]);

  const pick = useCallback(() => {
    if (!multiple) {
      setOpen(false);
    }
  }, [multiple, setOpen]);

  const calculatePosition = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = matchTriggerWidth ? Math.round(rect.width) : widthProp;
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    const wouldOverflowRight = rect.left + w > vw - 8;
    const vPart: React.CSSProperties = openAbove
      ? { bottom: vh - rect.top + offset, top: "auto" }
      : { top: rect.bottom + offset, bottom: "auto" };
    const hPart: React.CSSProperties = wouldOverflowRight
      ? { right: vw - rect.right, left: "auto" }
      : { left: rect.left, right: "auto" };
    setMenuStyle({
      position: "fixed",
      zIndex: 100000,
      width: w,
      maxHeight: "min(320px, 90vh)",
      borderRadius: "0.5rem",
      border: "1px solid #e2e8f0",
      backgroundColor: "#ffffff",
      boxShadow:
        "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
      display: "flex",
      flexDirection: "column" as const,
      overflow: "hidden",
      ...vPart,
      ...hPart,
    });
  }, [widthProp, matchTriggerWidth, estimatedHeight, offset]);

  const toggle = useCallback(() => {
    if (!open) {
      requestAnimationFrame(() => calculatePosition());
    }
    setOpen(!open);
  }, [open, setOpen, calculatePosition]);

  const onRootClick: React.MouseEventHandler = (e) => {
    toggle();
  };

  const onRootKeyDown: React.KeyboardEventHandler = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  useLayoutEffect(() => {
    if (open) {
      calculatePosition();
    }
  }, [open, calculatePosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => calculatePosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, calculatePosition]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!open) return;
      if (
        menuRef.current &&
        (menuRef.current.contains(target) || rootRef.current?.contains(target))
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [open, setOpen]);

  return (
    <div
      ref={rootRef}
      id={listboxId}
      className={`relative w-full max-w-full cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#205f43]/30 ${triggerClassName}`}
      onClick={onRootClick}
      onKeyDown={onRootKeyDown}
      role="combobox"
      tabIndex={0}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-controls={open ? `${listboxId}-listbox` : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {renderTrigger({ open, toggle, close })}

      {open ? (
        <div
          ref={menuRef}
          className={menuClassName}
          style={menuStyle}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          id={`${listboxId}-listbox`}
          role="listbox"
          aria-multiselectable={multiple}
        >
          {renderHeader ? (
            <div className="shrink-0 border-b border-slate-200 bg-slate-50">
              {renderHeader({ close })}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
            {renderMenu({ close, pick, multiple, open })}
          </div>

          {renderFooter ? (
            <div className="shrink-0 border-t border-slate-200 bg-slate-50">
              {renderFooter({ close })}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
